import { api } from '@/lib/api';
import type { CatalogImage } from '@/features/catalog-image/types';
import type { ShopProduct } from '@/features/shop/types';
import type { ShopVertical } from '@/features/shop/types';
import type { UnavailableReason } from '@/features/product-variant/types';

export type FavouriteVendor = {
  id: string;
  created_at: string;
  vendor: {
    id: string;
    business_name: string | null;
    city: string | null;
    module_types: string[];
    logo_s3_key: string | null;
    avg_rating: string;
    review_count: number;
  };
};

export type BookmarkedService = {
  id: string;
  created_at: string;
  service: {
    id: string;
    name: string;
    module_type: string;
    duration_minutes: number | null;
    price_inr: string;
    is_active: boolean;
    /**
     * The service's own photos, same shape and same paths Discovery sends.
     *
     * Saved services used to render as a generic wrench icon because the API carried no
     * images at all — a shopper saw a photo while browsing and a blank card once they saved
     * it. Optional because older responses predate the field.
     */
    images?: CatalogImage[];
    vendor: { id: string; business_name: string | null; city: string | null } | null;
  };
};

export async function fetch_favourite_vendors(): Promise<FavouriteVendor[]> {
  const res = await api.get<FavouriteVendor[]>('/user/favourites/vendors');
  return res.data;
}

/** Idempotent — PUT, so a retried tap is not a new row. */
export async function favourite_vendor(vendor_id: string): Promise<FavouriteVendor> {
  const res = await api.put<FavouriteVendor>(`/user/favourites/vendors/${vendor_id}`);
  return res.data;
}

export async function unfavourite_vendor(vendor_id: string): Promise<{ removed: boolean }> {
  const res = await api.delete<{ removed: boolean }>(`/user/favourites/vendors/${vendor_id}`);
  return res.data;
}

export async function fetch_bookmarked_services(): Promise<BookmarkedService[]> {
  const res = await api.get<BookmarkedService[]>('/user/favourites/services');
  return res.data;
}

export async function bookmark_service(service_id: string): Promise<BookmarkedService> {
  const res = await api.put<BookmarkedService>(`/user/favourites/services/${service_id}`);
  return res.data;
}

export async function unbookmark_service(service_id: string): Promise<{ removed: boolean }> {
  const res = await api.delete<{ removed: boolean }>(`/user/favourites/services/${service_id}`);
  return res.data;
}

/**
 * A favourited product.
 *
 * `product` carries the whole card — name, brand, price with the platform fee applied, photo
 * gallery, per-size availability — off the same serializer the Shop feed uses, so a favourite
 * cannot disagree with the shop about the same product.
 *
 * It is NULL once the product has been deleted; the row is deliberately kept, because
 * "no longer sold" answers "where did my saved item go" better than the row vanishing.
 */
export type FavouriteProduct = {
  id: string;
  created_at: string;
  vertical: 'FOOD' | 'PHARMACY';
  product_id: string;
  product: ShopProduct | null;
  /** False when the product is deleted, paused, sold out, or its vendor has gone away. */
  is_available: boolean;
  unavailable_reason: UnavailableReason | 'NO_LONGER_SOLD' | null;
};

export async function fetch_favourite_products(): Promise<FavouriteProduct[]> {
  const res = await api.get<FavouriteProduct[]>('/user/favourites/products');
  return res.data;
}

export async function favourite_product(
  vertical: ShopVertical,
  product_id: string,
): Promise<FavouriteProduct> {
  const res = await api.put<FavouriteProduct>(`/user/favourites/products/${vertical}/${product_id}`);
  return res.data;
}

export async function unfavourite_product(
  vertical: ShopVertical,
  product_id: string,
): Promise<{ removed: boolean }> {
  const res = await api.delete<{ removed: boolean }>(`/user/favourites/products/${vertical}/${product_id}`);
  return res.data;
}
